import React, { useEffect, useState } from "react";
import {
  Typography, Tooltip, Spin, Button, Toast, Modal, TextArea,
} from "@douyinfe/semi-ui";
import {
  IconArrowLeft, IconCopy, IconRefresh, IconEdit,
  IconUserCircle, IconHistogram, IconList,
  IconPriceTag, IconFlag, IconEyeOpened, IconPhone, IconInfoCircle, IconFile,
  IconAlertCircle, IconAlertTriangle,
} from "@douyinfe/semi-icons";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip as RechartsTooltip,
} from "recharts";
import { ApiClient, Lead } from "../../api/ApiClient";

const { Title, Text, Paragraph } = Typography;
const apiClient = ApiClient.getDefault();

/* ============= 设计 token ============= */
const RADIUS = { sm: 8, md: 12, lg: 16, xl: 20 } as const;
const SPACING = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24 } as const;
const FONT = { caption: 12, body: 13, bodyLg: 14, title: 15 } as const;

/* ============= 注入 CSS：keyframes / 伪元素 / className ============= */
const LP_CSS = `
@keyframes lpFadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lpPulseA {
  0%, 100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.55); }
  50%      { box-shadow: 0 0 0 8px rgba(22, 163, 74, 0); }
}
@keyframes lpBarGrow {
  from { width: 0 !important; }
}
@keyframes lpBlobDrift {
  0%, 100% { transform: translate(0, 0); }
  33%      { transform: translate(40px, -30px); }
  66%      { transform: translate(-30px, 20px); }
}

.lp-page {
  position: relative;
  min-height: 100vh;
  background: #eef2f7;
  overflow: hidden;
  padding: 24px 16px;
}
.lp-blob {
  position: fixed;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}
.lp-blob-1 {
  width: 480px; height: 480px;
  top: -120px; left: -80px;
  background: radial-gradient(circle, #c7e3ff 0%, transparent 70%);
  opacity: 0.7;
  animation: lpBlobDrift 18s ease-in-out infinite;
}
.lp-blob-2 {
  width: 520px; height: 520px;
  bottom: -160px; right: -100px;
  background: radial-gradient(circle, #fde2e4 0%, transparent 70%);
  opacity: 0.6;
  animation: lpBlobDrift 22s ease-in-out infinite reverse;
}
.lp-blob-3 {
  width: 380px; height: 380px;
  top: 40%; left: 55%;
  background: radial-gradient(circle, #fef3c7 0%, transparent 70%);
  opacity: 0.45;
  animation: lpBlobDrift 26s ease-in-out infinite;
}

.lp-content { position: relative; z-index: 1; }

.lp-card {
  position: relative;
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 8px 28px -12px rgba(15,23,42,0.14), 0 1px 2px rgba(15,23,42,0.04);
  transition: transform 0.28s cubic-bezier(.2,.8,.2,1), box-shadow 0.28s ease;
  margin-bottom: 16px;
  animation: lpFadeUp 0.55s ease both;
}
.lp-card::before {
  content: '';
  position: absolute;
  top: 0; left: 12%; right: 12%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent);
  pointer-events: none;
}
.lp-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 42px -12px rgba(15,23,42,0.2), 0 2px 4px rgba(15,23,42,0.06);
}
.lp-stagger-1 { animation-delay: 0.05s; }
.lp-stagger-2 { animation-delay: 0.12s; }
.lp-stagger-3 { animation-delay: 0.19s; }
.lp-stagger-4 { animation-delay: 0.26s; }
.lp-stagger-5 { animation-delay: 0.33s; }

.lp-num { font-variant-numeric: tabular-nums; }

.lp-score-block {
  position: relative;
  width: 72px; height: 72px;
  border-radius: 16px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  color: #fff;
  flex-shrink: 0;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.4),
    inset 0 -2px 6px rgba(0,0,0,0.12),
    0 6px 16px var(--lp-score-shadow, rgba(22,163,74,0.35));
}
.lp-score-block::after {
  content: '';
  position: absolute;
  top: 6px; left: 10%; right: 10%;
  height: 14px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.4), transparent);
  pointer-events: none;
}
.lp-score-num {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  text-shadow: 0 1px 0 rgba(255,255,255,0.35), 0 0 14px rgba(255,255,255,0.25);
  font-variant-numeric: tabular-nums;
}
.lp-score-cap {
  font-size: 10px;
  opacity: 0.9;
  margin-top: 3px;
  letter-spacing: 0.5px;
}

.lp-intention-A { animation: lpPulseA 2.2s ease-in-out infinite; }

.lp-dim-bar {
  height: 8px;
  background: rgba(226, 232, 240, 0.7);
  border-radius: 999px;
  overflow: hidden;
}
.lp-dim-bar-fill {
  height: 100%;
  border-radius: 999px;
  animation: lpBarGrow 1.1s cubic-bezier(.2,.8,.2,1) both;
}

.lp-resist-chip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 13px;
  line-height: 1.55;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.lp-resist-chip:hover {
  transform: translateX(2px);
  box-shadow: 0 4px 10px -2px rgba(239,68,68,0.2);
}

.lp-script-body {
  font-size: 13px;
  line-height: 1.75;
  color: #0c4a6e;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 14px 16px;
  border-radius: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  border: 1px solid #bae6fd;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.6);
}
`;

/* ============= 类型定义 ============= */
interface ProfileDimension {
  name: string;
  score: number;
  reason: string;
}

interface ProfileMapping {
  性别: string; 婚姻: string; 工作: string; 公司: string; 工作年限: string;
  收入: string; 房产: string; 车辆: string; 公积金: string; 社保: string;
  信用: string; 贷款类型: string; 年龄: string;
}

interface CustomerProfile {
  customer_name: string;
  total_score: number;
  star: number;
  ai_conclusion: string;
  dimensions: ProfileDimension[];
  profile_mapping: ProfileMapping;
  tags: string[];
  intention_level?: "A" | "B" | "C" | "D" | "E";
  intention_tags?: string[];
  resistance_points?: string[];
  insight: string;
  follow_up_script: string;
}

/* ============= 颜色映射 ============= */
const getScoreColor = (s: number): string => {
  if (s >= 70) return "#16a34a";
  if (s >= 40) return "#ea580c";
  return "#dc2626";
};
const getScoreLabel = (s: number): string => {
  if (s >= 70) return "优质客户";
  if (s >= 40) return "需关注";
  return "高风险";
};
const getDimBarColor = (s: number): string => {
  if (s <= 30) return "#ef4444";
  if (s <= 60) return "#f97316";
  return "#3b82f6";
};
const getIntentionColor = (lvl?: string): string => {
  switch (lvl) {
    case "A": return "#16a34a";
    case "B": return "#ca8a04";
    case "C": return "#ea580c";
    case "D": return "#dc2626";
    default: return "#6b7280";
  }
};
const getIntentionLabel = (lvl?: string): string => {
  switch (lvl) {
    case "A": return "A · 高意向";
    case "B": return "B · 中意向";
    case "C": return "C · 低意向";
    case "D": return "D · 无意向";
    case "E": return "E · 待定";
    default: return "未评估";
  }
};

/* ============= 星级 ============= */
const renderStars = (star: number) => {
  const arr = [];
  for (let i = 0; i < 5; i++) {
    arr.push(
      <span key={i} style={{
        color: i < star ? "#facc15" : "#e2e8f0",
        fontSize: FONT.caption,
        textShadow: i < star ? "0 1px 2px rgba(245,158,11,0.3)" : "none",
      }}>★</span>
    );
  }
  return <span style={{ display: "inline-flex", gap: 2 }}>{arr}</span>;
};

/* ============= 相对时间 ============= */
const formatRelativeTime = (iso?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(+d)) return null;
  const diff = (Date.now() - +d) / 60000;
  if (diff < 1) return "刚刚";
  if (diff < 60) return `${Math.floor(diff)} 分钟前`;
  if (diff < 1440) return `${Math.floor(diff / 60)} 小时前`;
  if (diff < 43200) return `${Math.floor(diff / 1440)} 天前`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/* ============= 样式片段 ============= */
const sectionTitle: React.CSSProperties = {
  fontSize: FONT.title,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  gap: SPACING.sm,
  marginBottom: SPACING.md,
  color: "#0f172a",
  letterSpacing: 0.2,
};

/* ============= 主组件 ============= */
export interface LeadProfileProps {
  leadId: number;
  onClose?: () => void;
}

export function LeadProfile({ leadId, onClose }: LeadProfileProps) {
  const [lead, setLead] = useState<Lead & { updatedAt?: string }>();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rescoring, setRescoring] = useState(false);

  const loadProfile = async () => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const [l, profileResp] = await Promise.all([
        apiClient.getLead(leadId),
        apiClient.request<{ stdRemark?: string }>("/qualification/profile", "GET", { leadId }),
      ]);
      setLead(l);
      const raw = profileResp.data?.stdRemark;
      if (raw) {
        try { setProfile(JSON.parse(raw)); } catch { setProfile(null); }
      } else {
        setProfile(null);
      }
    } catch (e: any) {
      setError(e?.message || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.document.title = "客户画像";
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleRescore = (mode: 'dict' | 'text') => {
    Modal.confirm({
      title: '重新 AI 评分',
      content: `将使用${mode === 'dict' ? '字典模式' : '文本模式'}重新生成客户画像，是否继续？`,
      onOk: async () => {
        setRescoring(true);
        try {
          await apiClient.request('/qualification/llmScore', 'POST', { leadId, mode });
          Toast.success('AI评分完成');
          await loadProfile();
        } catch (e: any) {
          Toast.error(e?.message || 'AI评分失败');
        } finally {
          setRescoring(false);
        }
      }
    });
  };

  const copyToClipboard = (text: string, hint = "已复制到剪贴板") => {
    navigator.clipboard.writeText(text).then(() => Toast.success(hint)).catch(() => Toast.info("请手动复制"));
  };

  const handleEditAndCopy = (original: string) => {
    let draft = original;
    Modal.confirm({
      title: "编辑话术后复制",
      content: (
        <TextArea
          defaultValue={original}
          autosize={{ minRows: 6, maxRows: 14 }}
          onChange={(v) => { draft = v; }}
          style={{ marginTop: SPACING.sm }}
        />
      ),
      okText: "复制",
      cancelText: "取消",
      onOk: () => { if (draft) copyToClipboard(draft, "编辑后的话术已复制"); },
    });
  };

  /* ====== 加载态 ====== */
  if (loading) {
    return (
      <div className="lp-page">
        <div className="lp-blob lp-blob-1" /><div className="lp-blob lp-blob-2" /><div className="lp-blob lp-blob-3" />
        <div className="lp-content" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <Spin size="large" />
        </div>
        <style>{LP_CSS}</style>
      </div>
    );
  }

  /* ====== 错误态 ====== */
  if (error && !lead) {
    return (
      <div className="lp-page">
        <div className="lp-blob lp-blob-1" /><div className="lp-blob lp-blob-2" /><div className="lp-blob lp-blob-3" />
        <div className="lp-content" style={{ maxWidth: 1100, margin: "0 auto" }}>
          {onClose && (
            <div style={{ marginBottom: SPACING.sm }}>
              <Button icon={<IconArrowLeft />} theme="borderless" onClick={onClose}>返回</Button>
            </div>
          )}
          <div className="lp-card lp-stagger-1" style={{ textAlign: "center", padding: "3rem 2rem" }}>
            <IconAlertCircle style={{ fontSize: 40, color: '#ef4444', marginBottom: SPACING.md }} />
            <Title heading={5} style={{ marginBottom: SPACING.sm }}>加载客户画像失败</Title>
            <Text type="tertiary">{error}</Text>
            <div style={{ marginTop: SPACING.lg }}>
              <Button onClick={loadProfile}>重试</Button>
            </div>
          </div>
        </div>
        <style>{LP_CSS}</style>
      </div>
    );
  }

  if (!lead) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Text>客户数据未找到</Text>
      </div>
    );
  }

  const generatedAt = formatRelativeTime(lead?.updatedAt);
  const scoreColor = getScoreColor(profile?.total_score ?? 0);
  const intentionColor = getIntentionColor(profile?.intention_level);

  /* ====== 空态 ====== */
  if (!profile) {
    return (
      <div className="lp-page">
        <div className="lp-blob lp-blob-1" /><div className="lp-blob lp-blob-2" /><div className="lp-blob lp-blob-3" />
        <div className="lp-content" style={{ maxWidth: 960, margin: "0 auto" }}>
          {onClose && (
            <div style={{ marginBottom: SPACING.sm }}>
              <Button icon={<IconArrowLeft />} theme="borderless" onClick={onClose}>返回</Button>
            </div>
          )}
          <div className="lp-card lp-stagger-1" style={{ textAlign: "center", padding: "3rem 2rem" }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', margin: '0 auto 16px',
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 28, fontWeight: 800, letterSpacing: 2,
              boxShadow: '0 8px 22px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}>AI</div>
            <Title heading={4} style={{ marginBottom: SPACING.sm }}>尚未生成 AI 画像</Title>
            <Paragraph style={{ color: "#475569", marginBottom: 20 }}>
              点击下方按钮，通过大模型分析生成完整的客户画像，包含 8 个维度评分、意向洞察、风险标签、决策建议和跟进话术。
            </Paragraph>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.lg, maxWidth: 640, margin: '0 auto 24px', textAlign: 'left' }}>
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #bfdbfe', borderRadius: RADIUS.md,
                padding: `${SPACING.lg}px 20px`,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
              }}>
                <div style={{ fontWeight: 700, fontSize: FONT.bodyLg, color: '#1d4ed8', marginBottom: SPACING.sm }}>
                  <IconList style={{ marginRight: 6 }} />字典模式（推荐）
                </div>
                <div style={{ fontSize: FONT.caption, color: '#475569', lineHeight: 1.6 }}>
                  将客户档案中的字典字段（性别、婚姻、工作、收入、房产、车辆、社保、征信等）拼接为结构化文本，
                  加上资质备注一起发送给大模型。数据更完整，画像更准确。
                </div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', border: '1px solid #c4b5fd', borderRadius: RADIUS.md,
                padding: `${SPACING.lg}px 20px`,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
              }}>
                <div style={{ fontWeight: 700, fontSize: FONT.bodyLg, color: '#6d28d9', marginBottom: SPACING.sm }}>
                  <IconFile style={{ marginRight: 6 }} />文本模式
                </div>
                <div style={{ fontSize: FONT.caption, color: '#475569', lineHeight: 1.6 }}>
                  仅使用客户的资质备注原文作为输入。适合备注信息已经足够详细的场景，
                  或者字典字段尚未完善填写的客户。
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: SPACING.sm, justifyContent: "center", marginBottom: SPACING.xl }}>
              <Button type="primary" size="large" loading={rescoring} onClick={() => handleRescore('dict')}>
                <IconList style={{ marginRight: 6 }} />字典模式生成
              </Button>
              <Button type="tertiary" size="large" loading={rescoring} onClick={() => handleRescore('text')}>
                <IconFile style={{ marginRight: 6 }} />文本模式生成
              </Button>
            </div>

            <div style={{ fontSize: FONT.caption, color: '#64748b', marginBottom: 10 }}>AI 将评估以下 8 个维度：</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              {[
                { t: "工作稳定性", c: "#2563eb", bg: "#dbeafe" },
                { t: "收入水平",   c: "#16a34a", bg: "#dcfce7" },
                { t: "房产资产",   c: "#7c3aed", bg: "#ede9fe" },
                { t: "车辆资产",   c: "#0891b2", bg: "#cffafe" },
                { t: "社保公积金", c: "#ea580c", bg: "#ffedd5" },
                { t: "信用状况",   c: "#dc2626", bg: "#fee2e2" },
                { t: "负债情况",   c: "#d97706", bg: "#fef3c7" },
                { t: "贷款意向",   c: "#db2777", bg: "#fce7f3" },
              ].map(({ t, c, bg }) => (
                <span key={t} style={{
                  padding: `4px ${SPACING.md}px`, fontSize: FONT.caption,
                  borderRadius: RADIUS.sm, fontWeight: 600,
                  background: bg, color: c,
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                }}>{t}</span>
              ))}
            </div>
          </div>
        </div>
        <style>{LP_CSS}</style>
      </div>
    );
  }

  /* ====== 有画像数据 ====== */
  const radarData = profile.dimensions.map(d => ({
    dimension: d.name, score: d.score, fullMark: 100,
  }));
  const hasIntention = !!(profile.intention_tags?.length || profile.resistance_points?.length || profile.intention_level);

  const IntentionBadge = ({ size = "sm" }: { size?: "sm" | "md" }) => {
    if (!profile.intention_level) return null;
    const isA = profile.intention_level === "A";
    const padding = size === "md" ? `4px ${SPACING.md}px` : `2px ${SPACING.sm}px`;
    const fontSize = size === "md" ? FONT.body : FONT.caption;
    return (
      <span
        className={isA ? "lp-intention-A" : undefined}
        style={{
          display: "inline-flex", alignItems: "center",
          padding, borderRadius: RADIUS.sm,
          background: `${intentionColor}1f`, color: intentionColor,
          border: `1px solid ${intentionColor}80`,
          fontSize, fontWeight: 700,
          letterSpacing: 0.3,
        }}
      >
        {getIntentionLabel(profile.intention_level)}
      </span>
    );
  };

  return (
    <div className="lp-page">
      <div className="lp-blob lp-blob-1" />
      <div className="lp-blob lp-blob-2" />
      <div className="lp-blob lp-blob-3" />

      <div className="lp-content" style={{ maxWidth: 1100, margin: "0 auto" }}>
        {onClose && (
          <div style={{ marginBottom: SPACING.sm }}>
            <Button icon={<IconArrowLeft />} theme="borderless" onClick={onClose}>返回</Button>
          </div>
        )}

        {/* ====== 1. 头部卡 ====== */}
        <div className="lp-card lp-stagger-1" style={{ padding: `${SPACING.lg}px ${SPACING.xl}px` }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: SPACING.lg }}>
            {/* 左：评分方块 */}
            <div className="lp-score-block" style={{
              background: `linear-gradient(135deg, ${scoreColor}, ${scoreColor}dd)`,
              ['--lp-score-shadow' as any]: `${scoreColor}59`,
            }}>
              <div className="lp-score-num">{profile.total_score}</div>
              <div className="lp-score-cap">综合评分</div>
            </div>

            {/* 中：客户 + 意向 + 星级 */}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: SPACING.sm, marginBottom: 6,
                flexWrap: "wrap",
              }}>
                <IconUserCircle style={{ fontSize: 18, color: '#2563eb', flexShrink: 0 }} />
                <span className="lp-num" style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
                  {profile.customer_name || lead.name}
                </span>
                <IntentionBadge size="md" />
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: SPACING.md,
                fontSize: FONT.caption, color: "#64748b", flexWrap: "wrap",
              }}>
                {renderStars(profile.star)}
                <span className="lp-num">
                  <IconHistogram size="small" style={{ marginRight: 4, verticalAlign: -2 }} />{profile.dimensions.length} 维评估
                </span>
                <span className="lp-num" style={{
                  padding: `2px ${SPACING.xs}px`, borderRadius: RADIUS.sm,
                  background: `${scoreColor}1f`, color: scoreColor, fontWeight: 600,
                }}>{getScoreLabel(profile.total_score)}</span>
                {generatedAt && <span>· 更新于 {generatedAt}</span>}
              </div>
            </div>

            {/* 右：操作按钮 */}
            <div style={{ display: "flex", gap: SPACING.sm }}>
              <Button icon={<IconRefresh />} type="warning" theme="light" size="small"
                loading={rescoring} onClick={() => handleRescore('dict')}>
                字典重评
              </Button>
              <Button icon={<IconRefresh />} type="tertiary" theme="light" size="small"
                loading={rescoring} onClick={() => handleRescore('text')}>
                文本重评
              </Button>
            </div>
          </div>

          {/* 档案标签 chips */}
          {profile.profile_mapping && (
            <div style={{
              display: "flex", flexWrap: "wrap", gap: SPACING.xs,
              marginTop: SPACING.md,
            }}>
              {Object.entries(profile.profile_mapping).filter(([, v]) => v).map(([key, val]) => (
                <Tooltip key={key} content={`${key}：${val}`}>
                  <div style={{
                    background: "rgba(255,255,255,0.55)", borderRadius: RADIUS.sm,
                    padding: `3px ${SPACING.sm}px`, fontSize: FONT.caption,
                    color: "#475569", border: "1px solid rgba(255,255,255,0.7)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                  }}>
                    <span style={{ color: "#94a3b8", marginRight: 4 }}>{key}</span>
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>{val}</span>
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {/* ====== 2. 雷达图 + 维度评分 ====== */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 5fr) minmax(0, 6fr)",
          gap: SPACING.lg, marginBottom: SPACING.lg,
        }}>
          <div className="lp-card lp-stagger-2" style={{ padding: SPACING.lg, marginBottom: 0 }}>
            <div style={sectionTitle}>
              <IconHistogram style={{ color: '#2563eb' }} /> 多维评估雷达
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11, fill: "#475569" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} tickCount={6} />
                <Radar
                  name="得分" dataKey="score"
                  stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.18} strokeWidth={2}
                  dot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 1.5 }}
                />
                <RechartsTooltip formatter={(value: number) => [`${value} 分`, "得分"]} />
              </RadarChart>
            </ResponsiveContainer>
            <div style={{ textAlign: "center", fontSize: FONT.caption, color: "#64748b", marginTop: SPACING.xs }}>
              各维度满分 100 · 低于 30 为高危
            </div>
          </div>

          <div className="lp-card lp-stagger-3" style={{ padding: SPACING.lg, marginBottom: 0 }}>
            <div style={sectionTitle}>
              <IconList /> 维度评分与说明
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: SPACING.sm }}>
              {profile.dimensions.map((dim, i) => {
                const barColor = getDimBarColor(dim.score);
                return (
                  <div key={i} style={{
                    background: "rgba(255,255,255,0.5)", borderRadius: RADIUS.md,
                    padding: `${SPACING.sm}px ${SPACING.md}px`,
                    border: "1px solid rgba(255,255,255,0.7)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                  }}>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "baseline",
                      marginBottom: 6,
                    }}>
                      <span style={{ fontSize: FONT.body, fontWeight: 600, color: "#0f172a" }}>{dim.name}</span>
                      <span className="lp-num" style={{
                        fontWeight: 700, fontSize: FONT.bodyLg,
                        color: dim.score <= 30 ? "#b91c1c" : dim.score <= 60 ? "#c2410c" : "#1e40af",
                      }}>
                        {dim.score}
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, marginLeft: 2 }}>/100</span>
                      </span>
                    </div>
                    <div className="lp-dim-bar" style={{ marginBottom: dim.reason ? 6 : 0 }}>
                      <div className="lp-dim-bar-fill" style={{
                        width: `${Math.min(100, Math.max(0, dim.score))}%`,
                        background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
                        boxShadow: `0 0 8px ${barColor}66`,
                      }} />
                    </div>
                    {dim.reason && (
                      <div style={{ fontSize: FONT.caption, color: "#64748b", lineHeight: 1.5 }}>
                        {dim.reason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ====== 3. 意向洞察 ====== */}
        {hasIntention && (
          <div className="lp-card lp-stagger-3" style={{ padding: `${SPACING.lg}px ${SPACING.xl}px` }}>
            <div style={sectionTitle}>
              <IconFlag style={{ color: intentionColor }} /> 意向洞察
              <div style={{ marginLeft: "auto" }}><IntentionBadge size="md" /></div>
            </div>
            {profile.intention_tags?.length ? (
              <div style={{ marginBottom: SPACING.md }}>
                <div style={{ fontSize: FONT.caption, color: "#64748b", marginBottom: 6 }}>意向标签</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {profile.intention_tags.map((t, i) => (
                    <span key={i} style={{
                      padding: `3px ${SPACING.sm}px`, fontSize: FONT.caption,
                      borderRadius: RADIUS.sm,
                      background: `${intentionColor}1a`, color: intentionColor,
                      border: `1px solid ${intentionColor}40`,
                      fontWeight: 600,
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                    }}>{t}</span>
                  ))}
                </div>
              </div>
            ) : null}
            {profile.resistance_points?.length ? (
              <div>
                <div style={{ fontSize: FONT.caption, color: "#64748b", marginBottom: 6 }}>抗拒点</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {profile.resistance_points.map((r, i) => (
                    <div key={i} className="lp-resist-chip">
                      <IconAlertTriangle className="lp-resist-icon" style={{ fontSize: 14, color: '#dc2626', marginTop: 1 }} />
                      <span>{r}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* ====== 4. 风险特征 + AI 结论 + 决策洞察 ====== */}
        <div className="lp-card lp-stagger-4" style={{ padding: `${SPACING.lg}px ${SPACING.xl}px` }}>
          <div style={sectionTitle}>
            <IconPriceTag style={{ color: '#4f46e5' }} /> 风险特征与决策建议
          </div>

          {profile.tags?.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: SPACING.md }}>
              {profile.tags.map((tag, i) => (
                <span key={i} style={{
                  background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                  padding: `3px ${SPACING.sm}px`, borderRadius: RADIUS.sm,
                  fontSize: FONT.caption, fontWeight: 600, color: "#4338ca",
                  border: "1px solid #c7d2fe",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                }}>
                  # {tag}
                </span>
              ))}
            </div>
          ) : null}

          {profile.ai_conclusion && (
            <div style={{
              background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
              borderLeft: `4px solid #22c55e`,
              padding: `${SPACING.sm}px ${SPACING.md}px`, borderRadius: RADIUS.md,
              marginBottom: SPACING.sm,
              fontSize: FONT.body, color: "#166534", lineHeight: 1.6,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
            }}>
              <IconFlag style={{ marginRight: 6, color: '#16a34a', verticalAlign: -2 }} />
              <strong>AI 结论：</strong>{profile.ai_conclusion}
            </div>
          )}

          {profile.insight && (
            <div style={{
              background: "linear-gradient(135deg, #fffbeb, #fef3c7)",
              borderLeft: `4px solid #f97316`,
              padding: `${SPACING.sm}px ${SPACING.md}px`, borderRadius: RADIUS.md,
              fontSize: FONT.body, lineHeight: 1.6, color: "#92400e",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
            }}>
              <IconEyeOpened style={{ marginRight: 6, color: '#f97316', verticalAlign: -2 }} />
              <strong>决策洞察：</strong>{profile.insight}
            </div>
          )}
        </div>

        {/* ====== 5. 专属跟进话术 ====== */}
        {profile.follow_up_script && (
          <div className="lp-card lp-stagger-5" style={{ padding: `${SPACING.lg}px ${SPACING.xl}px` }}>
            <div style={sectionTitle}>
              <IconPhone style={{ color: '#0284c7' }} /> 专属跟进话术
              <div style={{ marginLeft: "auto", display: "flex", gap: SPACING.xs }}>
                <Button icon={<IconCopy />} size="small" type="primary" theme="solid"
                  onClick={() => copyToClipboard(profile.follow_up_script)}>
                  复制
                </Button>
                <Button icon={<IconEdit />} size="small" theme="outline" type="tertiary"
                  onClick={() => handleEditAndCopy(profile.follow_up_script)}>
                  编辑后复制
                </Button>
              </div>
            </div>
            <div className="lp-script-body">
              {profile.follow_up_script}
            </div>
            <div style={{
              fontSize: FONT.caption, marginTop: SPACING.sm, color: "#64748b",
              display: "flex", alignItems: "center", gap: SPACING.xs,
            }}>
              <IconInfoCircle size="small" />
              <span>可根据实际沟通微调 · 保持专业与同理心{generatedAt ? ` · 更新于 ${generatedAt}` : ""}</span>
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", fontSize: FONT.caption, color: "#94a3b8", padding: `${SPACING.md}px 0` }}>
          基于客户资质数据 · 仅供内部参考
        </div>
      </div>

      <style>{LP_CSS}</style>
    </div>
  );
}
